import { useEffect } from 'react';
import propTypes from 'prop-types';
import { Form } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft } from 'react-icons/fi';

import { Spinner } from '@kiper/ui';
import { Col, Title, Subtitle, ButtonSubmit } from '../styles';
import {
  StepHeader,
  BackButton,
  ProgressGroup,
  ProgressTrack,
  ProgressSegment,
  StepLabel,
  OtpRow,
  OtpCell,
  Feedback,
} from './styles';
import { useOtpInput } from './hooks';

const OTP_LENGTH = 6;

const LoginMfaVerify = ({
  errors,
  submitCount,
  isSubmitting,
  handleSubmit,
  setFieldValue,
  setCurrentStep,
  steps,
}) => {
  const [t] = useTranslation('login');
  // Só o erro vindo do backend (setFieldError('code','invalid')) exibe "código
  // incorreto". Erros do Yup (length/required) ao digitar/reentrar NÃO contam.
  const hasError = !!submitCount && errors.code === 'invalid';

  const {
    digits,
    fields,
    handleChange,
    handleKeyDown,
    handlePaste,
    registerRef,
    focusAt,
  } = useOtpInput({
    length: OTP_LENGTH,
    onChange: value => setFieldValue('code', value),
    onComplete: () => handleSubmit(),
  });

  useEffect(() => {
    focusAt(0);
  }, [focusAt]);

  return (
    <Col>
      <StepHeader>
        <BackButton
          type="button"
          onClick={() => setCurrentStep(steps.mfaEnroll)}
        >
          <FiChevronLeft size={16} />
          {t('two-step.back')}
        </BackButton>
        <ProgressGroup>
          <ProgressTrack>
            <ProgressSegment $done />
            <ProgressSegment $done />
            <ProgressSegment $done />
          </ProgressTrack>
          <StepLabel>
            {t('two-step.step-progress', { current: 3, total: 3 })}
          </StepLabel>
        </ProgressGroup>
      </StepHeader>

      <Title>{t('two-step.enroll.confirm-title')}</Title>
      <Subtitle>{t('two-step.enroll.confirm-subtitle')}</Subtitle>
      <Form noValidate onSubmit={handleSubmit}>
        <OtpRow $error={hasError}>
          {fields.map((field, index) => (
            <OtpCell
              key={field}
              ref={registerRef(index)}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              maxLength={1}
              value={digits[index]}
              $filled={!!digits[index]}
              $error={hasError}
              onChange={event => handleChange(index, event.target.value)}
              onKeyDown={event => handleKeyDown(index, event)}
              onPaste={handlePaste}
              aria-label={t('two-step.code-digit', { index: index + 1 })}
              autoFocus={index === 0}
            />
          ))}
        </OtpRow>
        <Feedback $error={hasError}>
          {hasError
            ? t('two-step.code-invalid')
            : t('two-step.enroll.confirm-hint')}
        </Feedback>
        <ButtonSubmit
          block
          color="primary"
          type="submit"
          loading={isSubmitting ? 1 : 0}
        >
          {t(isSubmitting ? 'submitting' : 'two-step.enroll.activate')}
          {isSubmitting && <Spinner size={14} />}
        </ButtonSubmit>
      </Form>
    </Col>
  );
};

export default LoginMfaVerify;

LoginMfaVerify.propTypes = {
  errors: propTypes.object.isRequired,
  submitCount: propTypes.number.isRequired,
  isSubmitting: propTypes.bool.isRequired,
  handleSubmit: propTypes.func.isRequired,
  setFieldValue: propTypes.func.isRequired,
  setCurrentStep: propTypes.func.isRequired,
  steps: propTypes.object.isRequired,
};
